import React, { useRef, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import Layout from "../../layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { apiData } from "../../services/api_service";
import {
  addStoreAPI,
  fetchStatusByParent,
  fetchStoreAPI,
  updateStoreAPI,
} from "../../config";
import { AlertMessage } from "../../components/AlertMessage";
import { Backdrop, CircularProgress } from "@mui/material";
import { emirates } from "../../components/constants";
import { LoadingButton } from "@mui/lab";

const StoreAdd = () => {
  const navigate = useNavigate();
  const [storeTypes, setStoreTypes] = useState([]);
  const [status, setStatus] = useState([]);
  const { storeId } = useParams();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStoreTypes = async () => {
      const result = await apiData("get", fetchStatusByParent, {
        intParentId: 222,
      });
      if (result.success) setStoreTypes(result.data);
    };
    fetchStoreTypes();
  }, []);

  useEffect(() => {
    const fetchStatusList = async () => {
      const result = await apiData("get", fetchStatusByParent, {
        intParentId: 1,
      });
      if (result.success) setStatus(result.data);
    };
    fetchStatusList();
  }, []);

  const validateUaePhone = (value, isRequired = true) => {
    if (!value) return isRequired ? "Mobile Number is required" : true;

    // Remove spaces/hyphens for clean checking
    const clean = value.replace(/[\s-]/g, "");

    // Accept any country's phone number: optional leading '+', 7-15 digits
    const phoneRegex = /^\+?[0-9]{7,15}$/;
    if (!phoneRegex.test(clean)) {
      return "Enter a valid phone number";
    }

    return true; // ✅ valid
  };

  // ✅ Validation schema
  const validationSchema = Yup.object({
    storeName: Yup.string()
      .trim()
      .required("Source Name is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),
    storeType: Yup.string().required("Source Type is required"),
    contactPerson: Yup.string()
      .trim()
      .required("Contact Person is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),
    phone: Yup.string()
      .required("Phone Number is required")
      .test("valid-uae-phone", (value, ctx) => {
        const result = validateUaePhone(value, true);
        return result === true ? true : ctx.createError({ message: result });
      }),
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Enter a valid email address"
      ),
    city: Yup.string().trim().required("City is required").matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),
    emirate: Yup.string().required("Emirate is required"),
    status: Yup.string().required("Status is required"),
  });

  const alertRef = useRef();

  const formik = useFormik({
    initialValues: {
      storeName: "",
      storeType: "",
      contactPerson: "",
      phone: "",
      email: "",
      city: "",
      emirate: "",
      status: 2,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (storeId) values.id = storeId;

        const response = storeId
          ? await apiData("put", updateStoreAPI, values)
          : await apiData("post", addStoreAPI, values);

        if (response.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({ message: response.msg, messageType: "success" })
          );
          setTimeout(() => navigate("/store/list"), 2000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({ message: response.msg, messageType: "error" })
          );
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: error.msg,
            messageType: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    async function fetchData() {
      const response = await apiData("get", fetchStoreAPI, { id: storeId });
      if (response.success) {
        const data = response?.data[0];
        formik.setValues({
          storeName: data?.store_name,
          storeType: data?.store_type,
          contactPerson: data?.contact_person,
          phone: data?.phone,
          email: data?.email,
          city: data?.city,
          emirate: data?.emirate,
          status: data?.status,
        });
      }
    }
    if (storeId) fetchData();
  }, [storeId]);

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={alertRef} />

        <Grid container spacing={3}>
          {/* Page Header */}
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              {storeId ? "Edit Source" : "Add Source"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Source</Typography>
              <Typography color="text.primary">
                {storeId ? "Edit" : "Add"}
              </Typography>
            </Breadcrumbs>
          </Grid>

          {/* Action Buttons */}
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              href="/store/list"
            >
              {" "}
              List Stores
            </LoadingButton>{" "}
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        {/* Form */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2, mt: 2 }}>
            <Box component="form" onSubmit={formik.handleSubmit} noValidate>
              <Grid container spacing={3}>
                {/* Store Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Source Name"
                    name="storeName"
                    value={formik.values.storeName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.storeName &&
                      Boolean(formik.errors.storeName)
                    }
                    helperText={
                      formik.touched.storeName && formik.errors.storeName
                    }
                  />
                </Grid>

                {/* Store Type */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Source Type"
                    name="storeType"
                    value={formik.values.storeType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.storeType &&
                      Boolean(formik.errors.storeType)
                    }
                    helperText={
                      formik.touched.storeType && formik.errors.storeType
                    }
                  >
                    {storeTypes.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Contact Person */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    name="contactPerson"
                    value={formik.values.contactPerson}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.contactPerson &&
                      Boolean(formik.errors.contactPerson)
                    }
                    helperText={
                      formik.touched.contactPerson &&
                      formik.errors.contactPerson
                    }
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>

                {/* Emirate */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Emirate"
                    name="emirate"
                    value={formik.values.emirate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.emirate && Boolean(formik.errors.emirate)
                    }
                    helperText={formik.touched.emirate && formik.errors.emirate}
                  >
                    {emirates.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* City */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={formik.touched.status && formik.errors.status}
                  >
                    {status.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Divider */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" mt={5} pr={2}>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                      {storeId ? "Update Source" : "Create Source"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default StoreAdd;
